/* eslint-disable */

import React, { useEffect, useState, useMemo } from 'react'
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { Container, ContentHeader, Title, ContentBody, AdminsGrid, AdminCard, AdminContent, AdminInfo, AdminActions, SearchContainer, PaginationContainer, PaginationInfo, PaginationControls, PageSizeControl } from './Admin.styled';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchAdmins,
  setPageSize,
  selectPageSize,
  selectShouldOpen,
  selectMessage,
  selectSeverity,
  setOpen,
  deleteAdmin,
  updateAdmin,
  selectAdmins,
  selectAdminRowCount,
  selectPage,
  setPage,
  loadEaterCache,
  loadRestaurantCache,
  loadReviewsCache,
  syncEatersToVoucherify,
  syncRestaurantsToVoucherify,
  generateRestaurantEmbeddings,
  rebuildEaterCaches,
  rebuildRestaurantCaches,
  expireCache,
  expireAllCaches,
  refreshViewCache,
  warmupGroupPredictions,
  loadCensusCache
} from './slice';
import PageContainer from '../../components/PageContainer/PageContainer';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { AccessRequest, Admin } from './types';

const Admins: React.FC = () => {
  const openServer = useAppSelector(selectShouldOpen);
  const dispatch = useAppDispatch();
  const adminsFromServer = useAppSelector(selectAdmins);
  const [admins, setAdmins] = useState(adminsFromServer);
  const rowCount = useAppSelector(selectAdminRowCount);
  const pageSize = useAppSelector(selectPageSize);
  const currPage = useAppSelector(selectPage);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editType, setEditType] = useState('');
  const [cacheMenuAnchor, setCacheMenuAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    dispatch(fetchAdmins(0));
  }, [dispatch])

  useEffect(() => {
    setAdmins(adminsFromServer)
  }, [adminsFromServer])

  const filteredAdmins = useMemo(() => {
    if (!searchQuery.trim()) {
      return admins ?? [];
    }
    const lowerQuery = searchQuery.toLowerCase();
    return admins?.filter((admin: Admin) =>
      (admin.name?.toLowerCase().includes(lowerQuery) ?? false) ||
      (admin.email?.toLowerCase().includes(lowerQuery) ?? false) ||
      (admin.type?.toLowerCase().includes(lowerQuery) ?? false) ||
      (admin.status?.toLowerCase().includes(lowerQuery) ?? false)
    ) ?? [];
  }, [admins, searchQuery]);

  const totalPages = Math.ceil(rowCount / pageSize);
  const startIndex = currPage * pageSize + 1;
  const endIndex = Math.min((currPage + 1) * pageSize, rowCount);
  const hasNextPage = currPage < totalPages - 1;
  const hasPreviousPage = currPage > 0;

  const setOpenServer = (open: boolean) => {
    dispatch(setOpen(open));
  }

  const message = useAppSelector(selectMessage);
  const severity = useAppSelector(selectSeverity);

  const handlePageSizeChange = (event: any) => {
    dispatch(setPageSize(event.target.value));
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      dispatch(setPage(currPage + 1));
      dispatch(fetchAdmins(currPage + 1));
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      dispatch(setPage(currPage - 1));
      dispatch(fetchAdmins(currPage - 1));
    }
  };

  const handleEdit = (admin: Admin) => {
    setEditingId(admin.adminID);
    setEditStatus(admin.status);
    setEditType(admin.type);
  };

  const handleSave = (admin: Admin) => {
    const req: AccessRequest = {
      status: editStatus,
      email: admin.email,
      type: editType
    }
    dispatch(updateAdmin(req));
    setEditingId(null);
  };

  const handleDelete = (admin: Admin) => {
    const req: AccessRequest = {
      email: admin.email
    }
    dispatch(deleteAdmin(req));
  };

  const handleCacheMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setCacheMenuAnchor(event.currentTarget);
  };

  const handleCacheMenuClose = (): void => {
    setCacheMenuAnchor(null);
  };

  const handleLoadAllCaches = (): void => {
    handleCacheMenuClose();
    void dispatch(loadRestaurantCache());
    void dispatch(loadEaterCache());
    void dispatch(loadReviewsCache());
    void dispatch(generateRestaurantEmbeddings());
  };

  return (
      <PageContainer title='Admins' open={openServer} setOpen={setOpenServer} message={message} severity={severity}>
        <Container>
          <ContentHeader>
            <Title>Admins</Title>
            <Button
              color="primary"
              onClick={handleCacheMenuOpen}
              aria-controls="cache-menu"
              aria-haspopup="true"
            >
              Cache Operations
            </Button>
            <Menu
              id="cache-menu"
              anchorEl={cacheMenuAnchor}
              open={Boolean(cacheMenuAnchor)}
              onClose={handleCacheMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleLoadAllCaches}>
                <ListItemText primary="Load All Caches" secondary="Restaurants, Eaters, Reviews + Embeddings" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(loadRestaurantCache()); }}>
                <ListItemText primary="Load Restaurants to Cache" />
              </MenuItem>
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(loadEaterCache()); }}>
                <ListItemText primary="Load Eaters to Cache" />
              </MenuItem>
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(loadReviewsCache()); }}>
                <ListItemText primary="Load Reviews to Cache" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(syncEatersToVoucherify()); }}>
                <ListItemText primary="Sync Eaters to Voucherify" />
              </MenuItem>
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(syncRestaurantsToVoucherify()); }}>
                <ListItemText primary="Sync Restaurants to Voucherify" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(generateRestaurantEmbeddings()); }}>
                <ListItemText primary="Generate Restaurant Embeddings" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(rebuildEaterCaches()); }}>
                <ListItemText primary="Rebuild Eater Caches" secondary="Sync + Load to Cache" />
              </MenuItem>
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(rebuildRestaurantCaches()); }}>
                <ListItemText primary="Rebuild Restaurant Caches" secondary="Sync + Embeddings + Load to Cache" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(expireAllCaches()); }}>
                <ListItemText primary="Expire All Caches" secondary="Clear all configured caches" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(expireCache('location')); }}>
                <ListItemText primary="Expire Location Cache" />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(refreshViewCache()); }}>
                <ListItemText primary="Refresh View Cache" />
              </MenuItem>
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(warmupGroupPredictions()); }}>
                <ListItemText primary="Refresh Group Recommendations Cache" />
              </MenuItem>
              <MenuItem onClick={() => { handleCacheMenuClose(); void dispatch(loadCensusCache()); }}>
                <ListItemText primary="Load Census Data" secondary="Populate fips embeddings cache" />
              </MenuItem>
            </Menu>
          </ContentHeader>

          <SearchContainer>
            <TextField
              label="Search admins"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, email, type, or status"
              fullWidth
              sx={{ maxWidth: '400px' }}
            />
          </SearchContainer>

          <ContentBody>
            <AdminsGrid>
              {filteredAdmins.map((admin) => (
                <AdminCard key={admin.adminID}>
                  <AdminContent>
                    <AdminInfo>
                      <div>
                        <strong>Name:</strong> {admin.name}
                      </div>
                      <div>
                        <strong>Email:</strong> {admin.email}
                      </div>
                      <div>
                        <strong>Type:</strong> {editingId === admin.adminID ? (
                          <Select
                            value={editType}
                            onChange={(e) => setEditType(e.target.value)}
                            size="small"
                          >
                            <MenuItem value="">Select Type</MenuItem>
                            <MenuItem value="EATER">Eater</MenuItem>
                            <MenuItem value="RESTAURANT">Restaurant</MenuItem>
                            <MenuItem value="SUPERADMIN">Super Admin</MenuItem>
                          </Select>
                        ) : admin.type}
                      </div>
                      <div>
                        <strong>Status:</strong> {editingId === admin.adminID ? (
                          <Select
                            value={editStatus}
                            onChange={(e) => setEditStatus(e.target.value)}
                            size="small"
                          >
                            <MenuItem value="">Select Status</MenuItem>
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="INACTIVE">Inactive</MenuItem>
                          </Select>
                        ) : admin.status}
                      </div>
                    </AdminInfo>
                  </AdminContent>
                  <AdminActions>
                    {editingId === admin.adminID ? (
                      <>
                        <Button variant="contained" color="primary" size="small" onClick={() => handleSave(admin)}>Save</Button>
                        <Button variant="outlined" size="small" onClick={() => setEditingId(null)}>Cancel</Button>
                      </>
                    ) : (
                      <>
                        <Button variant="outlined" size="small" onClick={() => handleEdit(admin)}>Edit</Button>
                        <Button variant="outlined" color="error" size="small" onClick={() => handleDelete(admin)}>Delete</Button>
                      </>
                    )}
                  </AdminActions>
                </AdminCard>
              ))}
            </AdminsGrid>
          </ContentBody>

          {rowCount > 0 && (
            <PaginationContainer>
              <PaginationInfo>
                Showing {rowCount > 0 ? startIndex : 0} to {endIndex} of {rowCount} admins
              </PaginationInfo>
              <PageSizeControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Per Page</InputLabel>
                  <Select
                    value={pageSize}
                    label="Per Page"
                    onChange={handlePageSizeChange}
                  >
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </PageSizeControl>
              <PaginationControls>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handlePreviousPage}
                  disabled={!hasPreviousPage}
                >
                  Previous
                </Button>
                <span>{currPage + 1} of {totalPages || 1}</span>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </PaginationControls>
            </PaginationContainer>
          )}
        </Container>
      </PageContainer>
  )
}

export default withAuthenticationRequired(Admins)
